import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { formatCurrency } from "utils/helperFunctions";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { FiX, FiEdit, FiPackage, FiPlus, FiMinus, FiTrash2, FiCheck } from "react-icons/fi";
import { useCartProductsQuery, useCartPackagesQuery } from "apis/campaignFlow";
import { useInternetPortalPricing } from "apis/marketingPrice";
import CartConfirmModal from "./CartConfirmModal";

// Ported from beleef-frontend's MarketingCart.js — same interaction model
// (toggleProduct/togglePackage/removeLine/addCustomLine/changeQuantity),
// same dynamic REA/Domain pricing, same mandatory-item injection and
// free-inclusion auto-add/remove, same selectedItems-as-pending-buffer
// pattern, same buildPayload() shape, so the backend's
// PUT /admin/cart/:propertyId needs zero payload translation.
//
// Genuinely different from the agent-facing version, not a corner cut:
// - No checkout/payment-link flow — an admin correcting a cart isn't paying
//   for it.
// - No "pre-fill a recommended package into an empty cart" path — this tool
//   only ever opens on a cart that already exists (possibly emptied by the
//   admin mid-edit, but never a brand new one), so that path is simply never
//   reachable, same as it is for beleef-frontend's own relaunch carts.
// - Confirm opens a modal (CartConfirmModal) before saving, explaining the
//   Campaign Flow side effect — beleef-frontend's Confirm saves directly.

const SINGLE_CATEGORIES = ["video", "auctioneer"];
const NO_QUANTITY_CATEGORIES = ["internetPortals", "imGroup"];
const allowsQuantity = (line) =>
  !line?.packageId && !NO_QUANTITY_CATEGORIES.includes(line?.category || "");

const isDynamic = (name) =>
  name?.includes("Realestate.com.au") || name?.includes("Domain.com.au");
const getDynKey = (name) =>
  name?.includes("Realestate.com.au") ? "rea" : name?.includes("Domain.com.au") ? "domain" : null;
const parsePrice = (v) => parseFloat(String(v).replace(/^\$|,/g, "")) || 0;
const isFreeProduct = (p) => p.price === 0 && !isDynamic(p.name);
const norm = (v) => String(v || "").trim().toLowerCase();
const getCompanyId = (p) => {
  const raw = p?.contractorCompanyId;
  if (!raw) return null;
  return typeof raw === "string" ? raw : raw._id || raw.$oid || raw.id || null;
};
const isFreeInclusion = (p) =>
  !!p?.isFreeInclusion && !!getCompanyId(p) && p.freeWithCategories?.length > 0;
const fmtCat = (c) =>
  !c
    ? "Other"
    : c === "imGroup"
      ? "IM Group"
      : c.replace(/([A-Z])/g, " $1").trim().replace(/\b\w/g, (x) => x.toUpperCase());

const lineQuantity = (line) => {
  const quantity = Number(line?.quantity);
  return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
};
const lineTotal = (line) => (Number(line?.price) || 0) * lineQuantity(line);

// Stable identity for a cart line — mirrors cartLineKey in
// beleef-backend/utils/cart.js. The agent contribution refers to lines by
// this key; the backend derives the amount from them and refuses any line
// already on a Fast Track payment link.
const cartLineKey = (line) => {
  if (line?.packageId) return `pkg:${line.packageId}`;
  if (line?.productId) return `prod:${line.productId}`;
  return `custom:${String(line?.name || "").trim().toLowerCase()}`;
};

// Who pays for a line — shown under its name once the contribution is on.
const ContributionPill = ({ state, onToggle }) => {
  if (state === "fastTracked") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-darkgray bg-lightgray border border-mediumgray rounded-full px-2 py-0.5">
        <FiCheck size={11} /> Fast tracked
      </span>
    );
  }
  const paying = state === "paying";
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={paying}
      className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 border transition-colors ${
        paying
          ? "bg-black text-white border-black"
          : "bg-white text-darkgray border-mediumgray hover:border-black hover:text-black"
      }`}
    >
      {paying ? <FiCheck size={11} /> : <FiPlus size={11} />} Agent pays
    </button>
  );
};

const makeProductLine = (p, price) => ({
  productId: p._id,
  name: p.name,
  price: price ?? p.price,
  category: p.category || "Other",
  quantity: 1,
  isFree: isFreeProduct(p),
  originalPrice: p.originalPrice || null,
  hasDiscount: p.hasDiscount || false,
  isMandatory: p.isMandatory || false,
  isDynamicPricing: isDynamic(p.name),
});
const makeCustomLine = ({ name, price }) => ({
  name: name.trim(),
  price: Number(price) || 0,
  category: "other",
  quantity: 1,
  isCustom: true,
});
const makePackageLine = (pkg) => ({
  packageId: pkg._id,
  name: pkg.name,
  price: Number(pkg.price) || 0,
  category: "package",
  items: (pkg.productIds || []).filter((p) => p && typeof p === "object").map((p) => p.name),
});
const toSavedLine = (line) =>
  line.packageId
    ? { packageId: line.packageId, name: line.name, price: line.price, category: "package", items: line.items }
    : line.isCustom
      ? { name: line.name, price: line.price, category: "other", quantity: lineQuantity(line), isCustom: true }
      : { productId: line.productId, name: line.name, price: line.price, category: line.category, quantity: lineQuantity(line) };

const QuantityStepper = ({ quantity, onChange, onRemove }) => {
  const atOne = quantity <= 1;
  const showBin = atOne && !!onRemove;
  return (
    <div className="flex items-center border border-mediumgray rounded-full flex-shrink-0">
      <button
        onClick={() => (showBin ? onRemove() : onChange(-1))}
        disabled={atOne && !showBin}
        className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
          showBin ? "text-darkgray hover:text-red-600" : "text-darkgray hover:text-black disabled:opacity-40 disabled:cursor-not-allowed"
        }`}
      >
        {showBin ? <FiTrash2 size={14} /> : <FiMinus size={14} />}
      </button>
      <span className="w-6 text-center text-sm font-medium tabular-nums">{quantity}</span>
      <button onClick={() => onChange(1)} className="w-8 h-8 flex items-center justify-center rounded-full text-darkgray hover:text-black transition-colors">
        <FiPlus size={14} />
      </button>
    </div>
  );
};

const CartRow = ({ line, onEdit, onRemove, onQuantityChange, priceNode, required, contribution }) => (
  <div
    className={`flex items-center gap-3 px-4 py-3 border-b border-mediumgray last:border-b-0 ${
      contribution?.state === "paying" ? "bg-lightgray/60" : ""
    }`}
  >
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-black flex items-center gap-1.5">
        {line.packageId && <FiPackage size={13} className="flex-shrink-0" />}
        <span>{line.name}</span>
        {line.isCustom && (
          <span className="text-[10px] font-semibold text-darkgray bg-lightgray border border-mediumgray rounded px-1.5 py-px flex-shrink-0">
            Custom
          </span>
        )}
        {required && (
          <span className="text-[10px] font-semibold text-darkgray bg-lightgray border border-mediumgray rounded px-1.5 py-px flex-shrink-0">
            Required
          </span>
        )}
      </div>
      {line.packageId && line.items?.length > 0 && (
        <div className="text-[11px] text-darkgray mt-0.5">{line.items.join(" · ")}</div>
      )}
      {lineQuantity(line) > 1 && !line.packageId && (
        <div className="text-[11px] text-darkgray mt-0.5">{formatCurrency(line.price)} each</div>
      )}
      {contribution && (
        <div className="mt-1.5">
          <ContributionPill state={contribution.state} onToggle={contribution.onToggle} />
        </div>
      )}
    </div>
    {onQuantityChange && <QuantityStepper quantity={lineQuantity(line)} onChange={onQuantityChange} onRemove={onRemove} />}
    <div className="text-sm font-semibold flex-shrink-0">{priceNode}</div>
    {onEdit && (
      <button onClick={onEdit} className="text-darkgray hover:text-black flex-shrink-0" aria-label={`Edit ${line.name}`}>
        <FiEdit size={15} />
      </button>
    )}
    {onRemove && !onQuantityChange && (
      <button onClick={onRemove} className="text-darkgray hover:text-red-600 flex-shrink-0" aria-label="Remove">
        <FiX size={18} />
      </button>
    )}
  </div>
);

const CustomLineModal = ({ isOpen, draft, onClose, onSave }) => {
  const isEdit = draft && draft !== "new";
  const { register, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: { name: "", price: "" } });

  useEffect(() => {
    if (!isOpen) return;
    reset({ name: isEdit ? draft.name || "" : "", price: isEdit ? String(draft.price ?? "") : "" });
  }, [isOpen, draft, isEdit, reset]);

  const onSubmit = (values) => {
    onSave({ name: values.name, price: values.price });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? "Edit item" : "Add custom item"} className="max-w-sm">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="form-label">Name</label>
          <input
            type="text"
            {...register("name", { required: "Name is required", validate: (v) => !!v.trim() || "Name is required" })}
            placeholder="e.g. Neighbour roof drone access"
            className={`form-input border ${errors.name ? "border-red-500" : "border-mediumgray"}`}
          />
          {errors.name && <p className="form-error-message">{errors.name.message}</p>}
        </div>
        <div>
          <label className="form-label">Price</label>
          <input
            type="number"
            min="0"
            step="0.01"
            {...register("price", {
              required: "Price is required",
              validate: (v) => (v !== "" && !Number.isNaN(Number(v)) && Number(v) >= 0) || "Enter a valid price",
            })}
            placeholder="0.00"
            className={`form-input border ${errors.price ? "border-red-500" : "border-mediumgray"}`}
          />
          {errors.price && <p className="form-error-message">{errors.price.message}</p>}
        </div>
        <div className="flex justify-end gap-3">
          <Button onClick={onClose} className="gray-button" type="button">Cancel</Button>
          <Button className="black-button" type="submit">{isEdit ? "Save" : "Add to cart"}</Button>
        </div>
      </form>
    </Modal>
  );
};

const CompanyHeader = ({ name }) => (
  <div className="mt-3 mb-2 first:mt-0">
    <span className="text-xs font-semibold text-black uppercase tracking-wide">{name}</span>
  </div>
);

const ProductRow = ({ product, selected, free, usedOther, locked, required, displayPrice, onSelect }) => (
  <button
    type="button"
    disabled={locked}
    onClick={() => !usedOther && !locked && onSelect()}
    className={`w-full flex items-center justify-between gap-3 border rounded-lg px-3 py-2.5 text-sm transition-all text-left ${
      locked
        ? "bg-lightgray opacity-60 cursor-not-allowed border-mediumgray"
        : usedOther
          ? "bg-lightgray opacity-50 cursor-not-allowed"
          : selected
            ? "bg-black text-white border-black"
            : free
              ? "bg-green-50 border-green-300 hover:border-green-500"
              : "bg-white border-mediumgray hover:border-darkgray"
    }`}
  >
    <span className="font-medium min-w-0 flex items-center gap-1.5">
      {product.name}
      {required && (
        <span
          className={`text-[10px] font-semibold rounded px-1.5 py-px border ${
            selected ? "text-white border-white/40" : "text-darkgray bg-white border-mediumgray"
          }`}
        >
          Required
        </span>
      )}
    </span>
    <span className="flex-shrink-0 flex items-center gap-1">
      {free ? (
        <span className={`font-bold ${selected ? "text-white" : "text-green-700"}`}>FREE</span>
      ) : (
        <>
          {formatCurrency(displayPrice ?? product.price)}
          {product.hasDiscount && (
            <span className={`text-[9px] font-medium ${selected ? "text-green-300" : "text-green-500"}`}>-5%</span>
          )}
        </>
      )}
    </span>
  </button>
);

// Group a category's products by company for the Add-items modal.
const groupByCompany = (items) => {
  const sys = items.filter((p) => !getCompanyId(p));
  const order = [];
  const byCo = {};
  items.forEach((p) => {
    const id = getCompanyId(p);
    if (!id) return;
    if (!byCo[id]) {
      byCo[id] = [];
      order.push(id);
    }
    byCo[id].push(p);
  });
  const groups = [];
  if (sys.length) groups.push({ companyId: null, name: null, items: sys });
  order.forEach((id) => groups.push({ companyId: id, name: null, items: byCo[id] }));
  return groups;
};

const AdminMarketingCart = ({ propertyId, property, cart, allCarts = {}, cartKey, onConfirm }) => {
  const { suburb, postcode } = property || {};
  const [selectedItems, setSelectedItems] = useState(cart.items || []);
  // Agent contribution: the LINES the agent pays for (by cartLineKey). The
  // amount is derived — here for display, and on the backend, which is the
  // authority. A cart saved before itemKeys existed opens with nothing marked.
  const [contributionKeys, setContributionKeys] = useState(
    () => new Set(cart.agentContribution?.itemKeys || []),
  );
  const [agentContribEnabled, setAgentContribEnabled] = useState(!!cart.agentContribution?.isEnabled);
  const [dynamicPrices, setDynamicPrices] = useState({});
  const [editingPrice, setEditingPrice] = useState(null);
  const [customDraft, setCustomDraft] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [showTabs, setShowTabs] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Local, not a shared mutation's isPending — a property can have two
  // simultaneously-editable cart slots, each with its own AdminMarketingCart
  // off the same mutation hook; a shared isPending would spin/disable both
  // Confirm buttons while only one cart is actually saving.
  const [isSaving, setIsSaving] = useState(false);
  const editedPrices = useRef({ rea: false, domain: false });
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setSelectedItems(cart.items || []);
    setContributionKeys(new Set(cart.agentContribution?.itemKeys || []));
    setAgentContribEnabled(!!cart.agentContribution?.isEnabled);
  }, [cart]);

  // Lines already on the agent's Fast Track payment link (from the cart
  // detail endpoint) — locked out of the contribution here, refused on save.
  const fastTrackedKeys = useMemo(
    () => new Set(cart.fastTrackedKeys || []),
    [cart.fastTrackedKeys],
  );

  // Cross-cart: free product ids already used in another cart on this
  // property (allCarts is every cart keyed by sequence, cartKey this one's).
  const otherCartFreeIds = useMemo(
    () =>
      new Set(
        Object.entries(allCarts)
          .filter(([key]) => key !== cartKey)
          .flatMap(([, c]) => c?.items || [])
          .filter((i) => i.price === 0 && i.productId)
          .map((i) => i.productId),
      ),
    [allCarts, cartKey],
  );

  const { data: productsData = [], isPending: productsLoading } = useCartProductsQuery(propertyId);
  const { data: packagesList = [] } = useCartPackagesQuery(propertyId);
  const { data: portalData, isPending: portalLoading } = useInternetPortalPricing(
    property?.price,
    suburb,
    postcode,
  );

  const products = useMemo(() => {
    const g = productsData.reduce((a, p) => {
      (a[p.category || "Other"] ??= []).push(p);
      return a;
    }, {});
    return Object.entries(g).map(([category, items]) => ({ category, items }));
  }, [productsData]);

  const flat = useMemo(() => products.flatMap((c) => c.items), [products]);
  const selectedPackageIds = useMemo(
    () => new Set(selectedItems.map((i) => i.packageId).filter(Boolean)),
    [selectedItems],
  );
  const subtotal = useMemo(() => selectedItems.reduce((s, i) => s + lineTotal(i), 0), [selectedItems]);
  // Paid lines only — there is nothing to contribute on a FREE line.
  const contributableLines = useMemo(
    () => selectedItems.filter((line) => lineTotal(line) > 0),
    [selectedItems],
  );
  // Keys only count while the line is still in the cart; removing a line
  // silently drops it from the contribution, same as the backend does.
  const agentContribution = useMemo(
    () =>
      agentContribEnabled
        ? contributableLines
            .filter((line) => contributionKeys.has(cartLineKey(line)))
            .reduce((s, line) => s + lineTotal(line), 0)
        : 0,
    [agentContribEnabled, contributableLines, contributionKeys],
  );
  const contributionItemKeys = useMemo(
    () => contributableLines.map(cartLineKey).filter((key) => contributionKeys.has(key)),
    [contributableLines, contributionKeys],
  );
  const total = useMemo(() => Math.max(0, subtotal - agentContribution), [subtotal, agentContribution]);

  const contributionError = useMemo(() => {
    if (!agentContribEnabled) return null;
    const clash = contributableLines.filter(
      (line) => contributionKeys.has(cartLineKey(line)) && fastTrackedKeys.has(cartLineKey(line)),
    );
    if (clash.length) return `${clash.map((l) => l.name).join(", ")} already fast tracked — untick it.`;
    return null;
  }, [agentContribEnabled, contributableLines, contributionKeys, fastTrackedKeys]);

  const toggleContribution = (line) => {
    const key = cartLineKey(line);
    setContributionKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const contributionFor = (line) => {
    if (!agentContribEnabled || lineTotal(line) <= 0) return null;
    const key = cartLineKey(line);
    const state = fastTrackedKeys.has(key) ? "fastTracked" : contributionKeys.has(key) ? "paying" : "vendor";
    return { state, onToggle: () => toggleContribution(line) };
  };

  // Mandatory items must be present before confirming — matches
  // beleef-frontend's own rule exactly (enforceMandatory defaults true here
  // since this tool is never used on a locked/completed agreement's frozen
  // cart — those show the read-only tier banner instead, never this editor).
  const missingMandatory = useMemo(() => {
    const presentIds = new Set(selectedItems.map((i) => i.productId).filter(Boolean));
    return flat.filter((p) => p.isMandatory && !presentIds.has(p._id));
  }, [flat, selectedItems]);
  const mandatoryError =
    missingMandatory.length > 0
      ? `${missingMandatory.map((p) => p.name).join(", ")} ${missingMandatory.length > 1 ? "are" : "is"} required.`
      : null;

  const canConfirm = contributionError === null && mandatoryError === null;

  // ─── Dynamic price (REA/Domain) ───
  const dynPrice = useCallback(
    (name) => {
      const k = getDynKey(name);
      return k ? dynamicPrices[k] || 0 : null;
    },
    [dynamicPrices],
  );
  const productPrice = useCallback(
    (p) => (isDynamic(p.name) ? (dynPrice(p.name) ?? p.price) : p.price),
    [dynPrice],
  );

  // Portal dynamic prices.
  useEffect(() => {
    if (!portalData?.success || !portalData.data.items?.length) return;
    const prices = {};
    portalData.data.items.forEach((item) => {
      const k = getDynKey(item.name);
      if (!k) return;
      const np = parsePrice(item.price);
      const cur = dynamicPrices[k];
      prices[k] = editedPrices.current[k] && cur > 0 && !isNaN(cur) ? cur : np;
    });
    setDynamicPrices(prices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalData]);

  // Sync dynamic prices into selected product lines. Only touches items
  // flagged isDynamicPricing (freshly built from the catalogue this session)
  // — an existing saved line keeps its stored price untouched.
  useEffect(() => {
    if (Object.keys(dynamicPrices).length === 0) return;
    setSelectedItems((prev) => {
      let changed = false;
      const updated = prev.map((item) => {
        if (!item.isDynamicPricing) return item;
        const k = getDynKey(item.name);
        if (!k || !dynamicPrices[k] || item.price === dynamicPrices[k]) return item;
        changed = true;
        return { ...item, price: dynamicPrices[k] };
      });
      return changed ? updated : prev;
    });
  }, [dynamicPrices]);

  // Free inclusions — add a company's giveaway as soon as that company has a
  // trigger-category item in the cart, drop it again when the trigger goes.
  const productById = useMemo(() => new Map(flat.map((p) => [p._id, p])), [flat]);
  const inclusionProducts = useMemo(() => flat.filter(isFreeInclusion), [flat]);
  const inclusionProductIds = useMemo(() => new Set(inclusionProducts.map((p) => p._id)), [inclusionProducts]);
  useEffect(() => {
    if (inclusionProducts.length === 0) return;
    setSelectedItems((prev) => {
      const triggers = new Map();
      prev.forEach((i) => {
        if (i.packageId || inclusionProductIds.has(i.productId)) return;
        const product = productById.get(i.productId);
        const company = getCompanyId(product);
        const category = norm(i.category || product?.category);
        if (!company || !category) return;
        if (!triggers.has(company)) triggers.set(company, new Set());
        triggers.get(company).add(category);
      });

      const qualifies = (p) => {
        const cats = p ? triggers.get(getCompanyId(p)) : null;
        return !!cats && p.freeWithCategories.some((c) => cats.has(norm(c)));
      };

      const kept = prev.filter(
        (i) => !inclusionProductIds.has(i.productId) || qualifies(productById.get(i.productId)),
      );
      const present = new Set(kept.map((i) => i.productId));
      const toAdd = inclusionProducts.filter(
        (p) => !present.has(p._id) && !otherCartFreeIds.has(p._id) && qualifies(p),
      );

      if (!toAdd.length && kept.length === prev.length) return prev;
      return [...kept, ...toAdd.map((p) => makeProductLine(p))];
    });
  }, [selectedItems, productById, inclusionProducts, inclusionProductIds, otherCartFreeIds]);

  // Force mandatory products into the cart, same as beleef-frontend.
  const mandatoryProductIds = useMemo(
    () => new Set(flat.filter((p) => p.isMandatory).map((p) => p._id)),
    [flat],
  );
  const mandatorySyncedRef = useRef(false);
  useEffect(() => {
    if (!flat.length || mandatoryProductIds.size === 0) return;
    setSelectedItems((prev) => {
      const currentIds = new Set(prev.map((i) => i.productId));
      const toAdd = flat
        .filter((p) => p.isMandatory && !currentIds.has(p._id))
        .map((p) => makeProductLine(p, productPrice(p)));
      if (!toAdd.length) return prev;
      mandatorySyncedRef.current = true;
      return [...prev, ...toAdd];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, flat, mandatoryProductIds, productPrice]);

  // ─── Cart actions ───
  const togglePackage = useCallback((pkg) => {
    setSelectedItems((prev) =>
      prev.some((i) => i.packageId === pkg._id)
        ? prev.filter((i) => i.packageId !== pkg._id)
        : [...prev, makePackageLine(pkg)],
    );
  }, []);

  const toggleProduct = useCallback(
    (product) => {
      if (product.isMandatory) return; // can't toggle off a required item
      if (isFreeProduct(product) && otherCartFreeIds.has(product._id)) return;
      setSelectedItems((prev) => {
        if (prev.some((i) => i.productId === product._id)) {
          return prev.filter((i) => i.productId !== product._id);
        }
        const cat = (product.category || "").toLowerCase();
        const base = SINGLE_CATEGORIES.includes(cat)
          ? prev.filter((i) => i.packageId || (i.category || "").toLowerCase() !== cat)
          : prev;
        return [...base, makeProductLine(product, productPrice(product))];
      });
    },
    [otherCartFreeIds, productPrice],
  );

  const removeLine = useCallback((line) => {
    setSelectedItems((prev) =>
      line.packageId
        ? prev.filter((i) => i.packageId !== line.packageId)
        : line.isCustom
          ? prev.filter((i) => i !== line)
          : prev.filter((i) => i.productId !== line.productId),
    );
  }, []);

  const addCustomLine = useCallback(({ name, price }) => {
    setSelectedItems((prev) => [...prev, makeCustomLine({ name, price })]);
  }, []);

  const changeQuantity = useCallback((line, delta) => {
    setSelectedItems((prev) =>
      prev.map((i) => (i === line ? { ...i, quantity: Math.max(1, lineQuantity(i) + delta) } : i)),
    );
  }, []);

  const updateCustomLine = useCallback((line, { name, price }) => {
    setSelectedItems((prev) =>
      prev.map((i) => (i === line ? { ...i, name: name.trim(), price: Number(price) || 0 } : i)),
    );
  }, []);

  const editDynamicPrice = useCallback((name, val) => {
    const parsed = Math.max(0, parseFloat(String(val).replace(/[$,]/g, "")) || 0);
    const k = getDynKey(name);
    if (k) {
      editedPrices.current[k] = parsed > 0;
      setDynamicPrices((p) => ({ ...p, [k]: parsed }));
    }
    setSelectedItems((prev) => prev.map((i) => (i.name === name ? { ...i, price: parsed } : i)));
  }, []);

  const buildPayload = () => ({
    sequence: cart.sequence,
    items: selectedItems.map(toSavedLine),
    agentContribution: {
      amount: agentContribution,
      isEnabled: agentContribEnabled,
      itemKeys: contributionItemKeys,
    },
  });

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const saved = await onConfirm(buildPayload());
      if (saved?.items) setSelectedItems(saved.items);
      if (saved?.agentContribution) {
        setContributionKeys(new Set(saved.agentContribution.itemKeys || []));
        setAgentContribEnabled(!!saved.agentContribution.isEnabled);
      }
      setShowConfirm(false);
    } finally {
      setIsSaving(false);
    }
  };

  const lineKey = (line, index) =>
    line.packageId ? `pkg-${line.packageId}` : line.isCustom ? `custom-${index}` : `prod-${line.productId}`;
  const isFreeLine = (line) => !line.packageId && !line.isCustom && line.price === 0;
  const isMandatoryLine = (line) =>
    !line.packageId && (line.isMandatory || mandatoryProductIds.has(line.productId));

  // Price node for a cart row — handles FREE and the inline-editable dynamic
  // (REA/Domain) price, exactly like beleef-frontend's rowPriceNode.
  const rowPriceNode = (line) => {
    if (line.packageId) return formatCurrency(line.price);
    if (isDynamic(line.name)) {
      return editingPrice === line.productId ? (
        <input
          type="number"
          step="0.01"
          defaultValue={line.price}
          autoFocus
          onBlur={(e) => {
            editDynamicPrice(line.name, e.target.value);
            setEditingPrice(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              editDynamicPrice(line.name, e.target.value);
              setEditingPrice(null);
            }
          }}
          className="w-20 text-right border border-mediumgray rounded px-1 py-0.5 text-sm outline-none"
        />
      ) : (
        <button onClick={() => setEditingPrice(line.productId)} className="flex items-center gap-1">
          {formatCurrency(line.price)}
          <FiEdit size={12} className="text-darkgray" />
        </button>
      );
    }
    if (line.price === 0) return <span className="text-green-700">FREE</span>;
    return (
      <span className="flex items-center gap-1">
        {formatCurrency(lineTotal(line))}
        {line.hasDiscount && <span className="text-[9px] font-medium text-green-500">-5%</span>}
      </span>
    );
  };

  if (productsLoading || portalLoading) {
    return <div className="text-center py-6 text-darkgray text-sm">Loading…</div>;
  }

  return (
    <div className="w-full">
      {selectedItems.length > 0 ? (
        <div className="border border-mediumgray rounded-xl overflow-hidden">
          {selectedItems.map((line, index) => (
            <CartRow
              key={lineKey(line, index)}
              line={line}
              priceNode={rowPriceNode(line)}
              required={isMandatoryLine(line)}
              contribution={contributionFor(line)}
              onEdit={line.isCustom ? () => setCustomDraft(line) : undefined}
              onQuantityChange={allowsQuantity(line) && !isFreeLine(line) ? (delta) => changeQuantity(line, delta) : undefined}
              onRemove={!isFreeLine(line) && !isMandatoryLine(line) ? () => removeLine(line) : undefined}
            />
          ))}
          {agentContribEnabled && agentContribution > 0 && subtotal > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-mediumgray">
              <span className="text-sm">Paid by agent (agent contribution)</span>
              <span className="text-sm font-semibold">- {formatCurrency(agentContribution)}</span>
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-3 border-t border-mediumgray bg-lightgray">
            <span className="text-sm font-bold text-black">
              {agentContribEnabled && agentContribution > 0 ? "Vendor pays" : "Total"}
            </span>
            <span className="text-sm font-bold text-black">{formatCurrency(total)}</span>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-darkgray rounded-xl py-8 text-center text-sm text-darkgray">
          Cart is empty
        </div>
      )}

      <div className="flex gap-3 mt-3">
        <button
          onClick={() => setShowTabs(true)}
          className="flex-1 py-3 border border-dashed border-darkgray rounded-xl text-sm text-darkgray transition-colors"
        >
          + Add items
        </button>
        <button
          onClick={() => setCustomDraft("new")}
          className="flex-1 py-3 border border-dashed border-darkgray rounded-xl text-sm text-darkgray transition-colors"
        >
          + Add custom item
        </button>
      </div>

      {/* Agent contribution — marked on the cart rows above. Lines already
          fast tracked are locked; the backend refuses them too. */}
      <div
        className={`mt-5 rounded-xl border p-4 transition-colors ${
          agentContribEnabled ? "border-black" : "border-mediumgray"
        }`}
      >
        <label className="flex items-start justify-between gap-4 cursor-pointer">
          <div className="min-w-0">
            <span className="block text-sm font-semibold text-black">Agent contribution</span>
            <span className="block text-xs text-darkgray mt-0.5">
              The agent pays for some of the marketing. Whatever is marked is taken off what the
              vendor is asked to fund.
            </span>
          </div>
          <input
            type="checkbox"
            checked={agentContribEnabled}
            onChange={(e) => setAgentContribEnabled(e.target.checked)}
            className="cursor-pointer shrink-0 mt-1 h-4 w-4 accent-black"
          />
        </label>
        {agentContribEnabled && (
          <div className="mt-3 pt-3 border-t border-mediumgray">
            {contributableLines.length === 0 ? (
              <p className="text-xs text-darkgray">
                Add paid items to the cart first, then mark the ones the agent pays for.
              </p>
            ) : contributionItemKeys.length === 0 ? (
              <p className="text-xs text-darkgray">
                Tap{" "}
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-darkgray border border-mediumgray rounded-full px-2 py-0.5 align-middle">
                  <FiPlus size={11} /> Agent pays
                </span>{" "}
                on the items the agent is covering.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-black text-white px-3 py-2">
                  <span className="block text-[11px] opacity-80">
                    Agent pays · {contributionItemKeys.length} item
                    {contributionItemKeys.length > 1 ? "s" : ""}
                  </span>
                  <span className="block text-base font-semibold">{formatCurrency(agentContribution)}</span>
                </div>
                <div className="rounded-lg bg-lightgray px-3 py-2">
                  <span className="block text-[11px] text-darkgray">Vendor pays</span>
                  <span className="block text-base font-semibold">{formatCurrency(total)}</span>
                </div>
              </div>
            )}
            {fastTrackedKeys.size > 0 && (
              <p className="text-xs text-darkgray mt-2">
                Items marked “Fast tracked” are already on the agent's Fast Track payment link, so
                they can't be contributed again.
              </p>
            )}
            {contributionError && <p className="text-xs text-red-600 mt-2">{contributionError}</p>}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end mt-4 gap-1">
        {mandatoryError && <p className="text-xs text-red-600">{mandatoryError}</p>}
        <Button onClick={() => setShowConfirm(true)} disabled={!canConfirm} className="black-button">
          Confirm
        </Button>
      </div>

      {/* Add Items Modal */}
      <Modal
        isOpen={showTabs && (products.length > 0 || packagesList.length > 0)}
        onClose={() => { setShowTabs(false); setActiveTab(null); }}
        title="Add items"
      >
        <div className="flex flex-wrap gap-2">
          {packagesList.length > 0 && (
            <button
              onClick={() => setActiveTab(activeTab === "packages" ? null : "packages")}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                activeTab === "packages" ? "bg-black text-white border-black" : "bg-white text-darkgray border-mediumgray hover:border-black hover:text-black"
              }`}
            >
              Packages
            </button>
          )}
          {products.map((cat, i) => (
            <button
              key={cat.category}
              onClick={() => setActiveTab(activeTab === i ? null : i)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                activeTab === i ? "bg-black text-white border-black" : "bg-white text-darkgray border-mediumgray hover:border-black hover:text-black"
              }`}
            >
              {fmtCat(cat.category)}
            </button>
          ))}
        </div>
        <hr className="my-3 border-mediumgray" />
        {activeTab === "packages" ? (
          <div className="space-y-3">
            {groupByCompany(packagesList).map((g) => (
              <div key={g.companyId || "system"}>
                <div className="space-y-2">
                  {g.items.map((pkg) => {
                    const selected = selectedPackageIds.has(pkg._id);
                    const members = (pkg.productIds || []).filter((p) => p && typeof p === "object").map((p) => p.name);
                    return (
                      <button
                        key={pkg._id}
                        type="button"
                        onClick={() => togglePackage(pkg)}
                        className={`w-full flex items-start justify-between gap-3 border rounded-lg px-3 py-2.5 text-sm transition-all text-left ${
                          selected ? "bg-black text-white border-black" : "bg-white border-mediumgray hover:border-darkgray"
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="font-medium flex items-center gap-1.5">
                            <FiPackage size={13} className="flex-shrink-0" />
                            {pkg.name}
                          </span>
                          {members.length > 0 && (
                            <span className={`block text-[11px] mt-0.5 ${selected ? "text-white/70" : "text-darkgray"}`}>
                              {members.join(" · ")}
                            </span>
                          )}
                        </span>
                        <span className="flex-shrink-0 font-semibold">{formatCurrency(pkg.price)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : activeTab !== null && products[activeTab] ? (
          <div className="space-y-3">
            {groupByCompany(products[activeTab].items).map((g) => (
              <div key={g.companyId || "system"}>
                {g.name && <CompanyHeader name={g.name} />}
                <div className="space-y-2">
                  {g.items.map((p) => (
                    <ProductRow
                      key={p._id}
                      product={p}
                      selected={selectedItems.some((i) => i.productId === p._id)}
                      free={isFreeProduct(p)}
                      usedOther={isFreeProduct(p) && otherCartFreeIds.has(p._id)}
                      locked={p.isMandatory}
                      required={p.isMandatory}
                      displayPrice={productPrice(p)}
                      onSelect={() => toggleProduct(p)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-darkgray text-center py-6">Pick a tab above to see items.</p>
        )}
        <Button onClick={() => { setShowTabs(false); setActiveTab(null); }} className="black-button w-full mt-4">
          Done
        </Button>
      </Modal>

      <CustomLineModal
        isOpen={!!customDraft}
        draft={customDraft}
        onClose={() => setCustomDraft(null)}
        onSave={(values) => (customDraft === "new" ? addCustomLine(values) : updateCustomLine(customDraft, values))}
      />

      <CartConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => handleConfirmSave().catch(() => {})}
        isSaving={isSaving}
        previousTotal={cart.total}
        newTotal={total}
        cfLinked={cart.tier !== "unlocked"}
      />
    </div>
  );
};

export default AdminMarketingCart;
